import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from "@/admin/services/adminService";
import { useAdminAuth } from "./useAdminAuth";

export interface BannerFormData {
  title_fr: string;
  title_ar: string;
  subtitle_fr?: string;
  subtitle_ar?: string;
  image_url?: string;
  image_storage_path?: string;
  image_alt_fr?: string;
  image_alt_ar?: string;
  background_color: string;
  text_color: string;
  cta_url?: string;
  cta_text_fr?: string;
  cta_text_ar?: string;
  cta_target: "_self" | "_blank";
  is_active: boolean;
  dismissible: boolean;
}

export function useBannerManagement() {
  const qc = useQueryClient();
  const { adminUserId } = useAdminAuth();

  const query = useQuery({
    queryKey: ["banners-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("announcement_banners")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async ({ id, formData }: { id?: string; formData: BannerFormData }) => {
      if (id) {
        const { data, error } = await supabase
          .from("announcement_banners")
          .update({ ...formData, updated_by_admin_id: adminUserId })
          .eq("id", id)
          .select()
          .single();
        if (error) throw error;
        return { data, isNew: false };
      } else {
        const { data, error } = await supabase
          .from("announcement_banners")
          .insert({ ...formData, created_by_admin_id: adminUserId })
          .select()
          .single();
        if (error) throw error;
        return { data, isNew: true };
      }
    },
    onSuccess: async ({ data, isNew }, { id }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: isNew ? "banner_created" : "banner_updated",
          targetType: "banner",
          targetId: data.id,
          notes: `Bandeau ${isNew ? "créé" : "mis à jour"} : ${data.title_fr}`,
          afterState: data,
        });
      }
      toast.success(`Bandeau ${isNew ? "créé" : "mis à jour"} avec succès`);
      qc.invalidateQueries({ queryKey: ["banners-admin"] });
      qc.invalidateQueries({ queryKey: ["active-banner"] });
    },
    onError: (err: Error) => toast.error(`Erreur : ${err.message}`),
  });

  const archiveMutation = useMutation({
    mutationFn: async (bannerId: string) => {
      const { data, error } = await supabase
        .from("announcement_banners")
        .update({
          archived_at: new Date().toISOString(),
          archived_by_admin_id: adminUserId,
          is_active: false,
        })
        .eq("id", bannerId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "banner_archived",
          targetType: "banner",
          targetId: data.id,
          notes: `Bandeau archivé : ${data.title_fr}`,
        });
      }
      toast.success("Bandeau archivé");
      qc.invalidateQueries({ queryKey: ["banners-admin"] });
      qc.invalidateQueries({ queryKey: ["active-banner"] });
    },
    onError: (err: Error) => toast.error(`Erreur : ${err.message}`),
  });

  return { query, upsertMutation, archiveMutation };
}
